import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Button } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { CodedException, ProjectDto } from '@/core';
import { useAddReportMutation } from '@/redux';
import { notify } from '@/components';
import { validateConstructor } from '../Projects.Constructor';
import { ReportGenerationForm } from './ReportGenerationForm';
import { downloadReport, fileToBlob } from './helpers';
import { FormValueType } from './hooks/useSchemaReport';
import classes from './ProjectGenerationPanel.module.scss';
import { ReportGenerationWarningModal } from './ReportGenerationWarningModal';

type ProjectGenerationPanelPropsType = {
  projectData: ProjectDto;
};

export const ProjectGenerationPanel = ({ projectData }: ProjectGenerationPanelPropsType) => {
  const navigate = useNavigate();
  const { t } = useTranslation(undefined, { keyPrefix: 'projects.project' });
  const validator = useMemo(() => validateConstructor(projectData), [projectData]);
  const isProjectValid = Object.values(validator).every((x) => x.isValid);
  const [addReport] = useAddReportMutation();

  const [showGenerateReportForm, setGenerateReportForm] = useState<boolean>(false);
  const [reportValidationErrors, setReportValidationErrors] = useState<string[]>([]);
  const [reportWarnings, setReportWarnings] = useState<string[]>([]);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState<boolean>(false);

  const navigateToConstructorPage = () => navigate(`/projects/constructor/${projectData.id}`);
  const toggleGenerateForm = () => {
    if (!isProjectValid) {
      notify.error(t('invalidProject'));
      return;
    }
    setGenerateReportForm((prev) => !prev);
    setExcelFiles([]);
  };
  const [excelFiles, setExcelFiles] = useState<File[]>([]);

  const showWarningModal = (warnings: string[]) => {
    setReportWarnings(warnings);
    setIsWarningModalOpen(true);
  };

  const closeWarningModal = () => {
    setIsWarningModalOpen(false);
    setReportWarnings([]);
  };

  const handleSubmit = async ({ name: reportName }: FormValueType) => {
    const filesToBlobPromises = excelFiles.map(fileToBlob);
    const sourceBlobs = await Promise.all(filesToBlobPromises);

    const reportData = new FormData();
    sourceBlobs.forEach((blob) => reportData.append('file', blob));
    try {
      const { reference, name, warnings } = await addReport({
        reportData,
        projectId: projectData.id,
        reportName,
      }).unwrap();

      downloadReport(reference, name, toggleGenerateForm);
      if (warnings?.[0]?.length) showWarningModal(warnings);
    } catch (e) {
      const error = e as CodedException;
      if (error.code === 'INV' && error.payload) {
        setReportValidationErrors(error.payload);
      } else {
        const coded = CodedException.from(error);
        notify.error.coded(coded);
      }
    }
  };

  return (
    <>
      {isWarningModalOpen ? (
        <ReportGenerationWarningModal
          warnings={reportWarnings}
          isModalOpen={isWarningModalOpen}
          closeModal={closeWarningModal}
        />
      ) : null}

      <div className={classes.controls}>
        <Button size="large" onClick={navigateToConstructorPage}>
          {t('editProjectBtn')}
        </Button>
        <Button size="large" type="primary" icon={<FileTextOutlined />} onClick={toggleGenerateForm}>
          {t('generateReportBtn')}
        </Button>
      </div>
      {showGenerateReportForm ? (
        <ReportGenerationForm
          isModalOpen={showGenerateReportForm}
          setIsModalOpen={(value: boolean) => {
            setGenerateReportForm(value);
            setReportValidationErrors([]);
          }}
          projectData={projectData}
          handleSubmit={handleSubmit}
          excelFiles={excelFiles}
          setExcelFiles={setExcelFiles}
          errors={reportValidationErrors}
        />
      ) : null}
    </>
  );
};
